import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { AddContactComponent } from 'src/app/components/contacts/add/add.component';
import { SaveEventAction } from 'src/app/enums/saveEventAction';
import { ApiService } from 'src/app/services/api.service';
import { ContactService } from 'src/app/services/contact.service';
import { ErrorManagerService } from 'src/app/services/error-manager.service';
import { Contact, MessageTitle, UserType } from '../../enums/constants';
import { UserService } from '../../services/user.service';

@Component({
    selector: 'app-contact-multiselect',
    templateUrl: './contact-multiselect.component.html',
    styleUrls: ['./contact-multiselect.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: ContactMultiselectComponent,
        }
    ],
    standalone: false
})
export class ContactMultiselectComponent implements OnInit, ControlValueAccessor, OnDestroy {
  @Input() actingAs: Contact.ACTOR_SPOUSE | Contact.ACTOR_EXECUTOR | Contact.ACTOR_PRIMARY_TRUSTEE | Contact.ACTOR_SECONDARY_TRUSTEE | Contact.ACTOR_PRIMARY_BENEFICIARY | Contact.ACTOR_SECONDARY_BENEFICIARY | Contact.ACTOR_INSURANCE_BENEFICIARY;
  @Input() required: boolean;
  @Input() editing: boolean;
  @Input() controlName: string;
  @Input() multiSelect: boolean = false;
  @Input() closeDropdownOnSelect: boolean = true;
  @Input() hideDisabled: boolean = true;
  @Input() hideSelected: boolean = false;
  @Input() parentBreadcrumbs: string;
  @Input() clearable: boolean = false;
  @Input() parentEvent: {
    hide: (value) => void,
    loading: (value) => void
  } = {
      hide(value) { },
      loading(value) { }
    };
  @Input() set disabledOptions(disabledOptionIds: number[]) {
    this.disabledOptionIds = new Set(disabledOptionIds);
    this.setOptionContacts();
  }
  @Input() vaultUserForAdmin: any;

  @Output() contactUnselected = new EventEmitter<any>();
  @Output() contactSelected = new EventEmitter<any>();
  @Output() newContactAdded = new EventEmitter<any>();

  touched: boolean = false;
  invalid: boolean = false;
  disabled: boolean = false;
  selected: any | any[] = null;
  optionContacts: any[] = [];
  disabledOptionIds: Set<number> = new Set();
  onChange = (value: any) => { };
  onTouched = (event: any) => { };

  private contactSaveEventsSubscription: Subscription;

  constructor(
    private apiService: ApiService,
    private matDialog: MatDialog,
    private contactService: ContactService,
    private errorManager: ErrorManagerService,
    private userService: UserService
  ) { }

  setOptionContacts() {
    this.optionContacts = this.apiService.allContacts.map(contact => ({ ...contact }));
    if (this.hideDisabled) {
      this.optionContacts = this.optionContacts.filter(contact => !this.disabledOptionIds.has(contact.id));
    } else {
      this.optionContacts = this.optionContacts.map(contact => ({ ...contact, disabled: this.disabledOptionIds.has(contact.id) }));
    }
    this.optionContacts = [...this.optionContacts].sort(this.contactCompare);
  }

  updateSelection() {
    if (this.multiSelect) {
      const selectedIds = new Set<number>(this.selected?.map(contact => contact.id));
      this.selected = this.optionContacts.filter(contact => selectedIds?.has(contact.id)).sort(this.contactCompare);
    } else {
      this.selected = this.optionContacts.find(contact => contact.id === this.selected?.id);
    }
    this.onSelectionChanged(this.selected);
  }

  ngOnInit(): void {
    if (this.optionContacts.length === 0) {
      this.setOptionContacts();
      this.updateSelection();
    }
    this.contactSaveEventsSubscription = this.contactService.contactSaveEvents$?.subscribe(eventData => {
      this.setOptionContacts();
      this.updateSelection();
    });
  }

  ngOnDestroy(): void {
    this.contactSaveEventsSubscription?.unsubscribe();
  }

  writeValue(input: number | number[]): void {
    if (!input) {
      return;
    }

    if (Array.isArray(input) !== this.multiSelect) {
      this.errorManager.showError(this.errorManager.getMessage(''), MessageTitle.ERROR);
    }

    if (this.multiSelect && Array.isArray(input)) {
      this.selected = [];
      for (let contactId of input) {
        const contact = this.optionContacts.find(option => option.id === contactId);
        if (contact) {
          this.selected.push(contact);
        }
      }
      this.selected = [...this.selected].sort(this.contactCompare);
    } else {
      this.selected = this.optionContacts.find(option => option.id === input);
    }
  }

  registerOnChange(onChange: any): void {
    this.onChange = onChange;
    if (!this.disabled) {
      this.onChange(this.getSelectedContactIds());
    }
  }

  registerOnTouched(onTouched: any): void {
    this.onTouched = onTouched;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onSelectionChanged(contacts: any | any[]): void {
    this.selected = contacts;
    if (!this.disabled) {
      this.onChange(this.getSelectedContactIds());
    }
  }

  markAsTouched(): void {
    if (!this.touched) {
      this.touched = true;
    }
  }

  checkAndSetClass(e: Event) {
    let type = e.type;
    let value = this.selected && this.getSelectedContactIds();
    let containerElement = document.querySelector("#contact-input-container");
    if (this.editing || value || type === 'focus') {
      containerElement.classList.add('inputted');
    } else {
      containerElement.classList.remove('inputted');
    }
    if (type === 'blur') {
      this.onTouched(e);
      this.markAsTouched();
    }
  }

  openAddContactModal(model) {
    const self = this;
    const breadcrumbs = this.parentBreadcrumbs || '';
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      editWillExecutor: this.actingAs === Contact.ACTOR_EXECUTOR,
      editPersonalSpouse: this.actingAs === Contact.ACTOR_SPOUSE,
      editTrustee: this.actingAs === Contact.ACTOR_PRIMARY_TRUSTEE || this.actingAs === Contact.ACTOR_SECONDARY_TRUSTEE,
      editBeneficiary: this.actingAs === Contact.ACTOR_PRIMARY_BENEFICIARY || this.actingAs === Contact.ACTOR_SECONDARY_BENEFICIARY || this.actingAs === Contact.ACTOR_INSURANCE_BENEFICIARY,
      breadcrumbs: breadcrumbs,
      openedAsFormInput: true
    };
    if (model) {
      dialogConfig.data.model = model;
    }

    if (this.userService.user.usertype === UserType.ADMIN) {
      dialogConfig.data = {
        ...dialogConfig.data,
        user: this.vaultUserForAdmin
      }
    }

    self.parentEvent.hide(true);
    let dialogRef = this.matDialog.open(AddContactComponent, dialogConfig);
    let onSaveEventSubscription = dialogRef.componentInstance.onSaveEvent.subscribe((data) => {
      let savedModel = data.model;
      let modelAction = data.action;
      if (modelAction === SaveEventAction.add) {
        if (self.multiSelect) {
          self.selected = [...self.selected, savedModel];
        } else {
          self.selected = savedModel;
        }
        self.setOptionContacts();
        self.updateSelection();
        self.newContactAdded.emit(savedModel);
        self.select(savedModel);
      }
    });
    dialogRef.afterClosed().subscribe((value) => {
      onSaveEventSubscription.unsubscribe();
      self.parentEvent.hide(false);
    });
  }

  unselect(unselectedContact) {
    if (this.multiSelect) {
      this.selected = this.selected.filter(selectedContact => selectedContact.id !== unselectedContact.id);
    } else {
      if (this.selected.id === unselectedContact.id) {
        this.selected = null;
      }
    }
    this.updateSelection();
    this.contactUnselected.emit(unselectedContact);
  }

  select(selectedContact) {
    this.contactSelected.emit(selectedContact);
    this.updateSelection();
  }

  searchContacts(term: string, item: any): boolean {
    term = term.toLocaleLowerCase();
    return item.first_name.toLocaleLowerCase().indexOf(term) !== -1 ||
      item.last_name.toLocaleLowerCase().indexOf(term) !== -1;
  }

  private getSelectedContactIds(): number | number[] {
    if (this.multiSelect) {
      const ids = this.selected?.map(contact => contact.id) ?? [];
      return ids;
    } else {
      return this.selected?.id ?? 0;
    }
  }

  contactCompare(contactLeft, contactRight) {
    if (contactLeft.first_name !== contactRight.first_name) {
      return contactLeft.first_name?.localeCompare(contactRight.first_name);
    } else {
      return contactLeft.last_name?.localeCompare(contactRight.last_name);
    }
  }
}
